import { useState, useEffect, useMemo, useCallback } from "react";
import { DataTable, StatusBadge } from "../../components/dashboard";
import { getUpdateStatus, publishVersion, triggerInstanceUpdate } from "../../lib/api/cloud";
import type { InstanceUpdateInfo } from "../../types/cloud";

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return "Just now";

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function UpdateManagement() {
  const [targetVersion, setTargetVersion] = useState<string | null>(null);
  const [instances, setInstances] = useState<InstanceUpdateInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Publish form state
  const [publishFormVersion, setPublishFormVersion] = useState("");
  const [publishGitRef, setPublishGitRef] = useState("");
  const [publishDescription, setPublishDescription] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Action state
  const [triggeringId, setTriggeringId] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      const res = await getUpdateStatus();
      setTargetVersion(res.targetVersion);
      setInstances(res.instances);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load update status";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publishFormVersion.trim()) return;

    setPublishing(true);
    setPublishSuccess(false);
    setPublishError(null);
    try {
      await publishVersion(
        publishFormVersion.trim(),
        publishGitRef.trim() || undefined,
        publishDescription.trim() || undefined
      );
      setPublishSuccess(true);
      setPublishFormVersion("");
      setPublishGitRef("");
      setPublishDescription("");
      await fetchStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to publish version";
      setPublishError(message);
    } finally {
      setPublishing(false);
    }
  };

  const handleTriggerUpdate = async (instanceId: string) => {
    setTriggeringId(instanceId);
    try {
      await triggerInstanceUpdate(instanceId);
      await fetchStatus();
    } catch {
      // Silently handle — status refresh will show current state
    } finally {
      setTriggeringId(null);
    }
  };

  const stats = useMemo(() => {
    const total = instances.length;
    const stripBuild = (v: string) => {
      const idx = v.indexOf("+");
      return idx >= 0 ? v.slice(0, idx) : v;
    };
    const onLatest = instances.filter(
      (i) =>
        i.currentVersion !== null &&
        targetVersion !== null &&
        stripBuild(i.currentVersion) === stripBuild(targetVersion)
    ).length;
    const withErrors = instances.filter((i) => i.updateStatus === "error").length;
    const inProgress = instances.filter(
      (i) => i.updateStatus === "downloading" || i.updateStatus === "updating"
    ).length;
    return { total, onLatest, withErrors, inProgress };
  }, [instances, targetVersion]);

  const columns = useMemo(
    () => [
      {
        key: "subdomain",
        label: "Subdomain",
        sortable: true,
        render: (i: InstanceUpdateInfo) => <span className="font-mono text-sm">{i.subdomain}</span>,
      },
      {
        key: "userEmail",
        label: "User",
        sortable: true,
        render: (i: InstanceUpdateInfo) => <span className="text-sm">{i.userEmail}</span>,
      },
      {
        key: "currentVersion",
        label: "Current",
        sortable: true,
        render: (i: InstanceUpdateInfo) => (
          <span className="font-mono text-sm text-[#999]">{i.currentVersion ?? "unknown"}</span>
        ),
      },
      {
        key: "targetVersion",
        label: "Target",
        sortable: true,
        render: (i: InstanceUpdateInfo) => (
          <span className="font-mono text-sm text-[#999]">{i.targetVersion ?? "—"}</span>
        ),
      },
      {
        key: "updateStatus",
        label: "Status",
        sortable: true,
        render: (i: InstanceUpdateInfo) => (
          <div className="flex flex-col gap-1">
            <StatusBadge status={i.updateStatus} />
            {i.updateStatus === "error" && i.errorMessage && (
              <span className="text-xs text-red-400 truncate max-w-[200px]" title={i.errorMessage}>
                {i.errorMessage}
              </span>
            )}
          </div>
        ),
      },
      {
        key: "lastCheckAt",
        label: "Last Check",
        sortable: true,
        render: (i: InstanceUpdateInfo) => (
          <span className="text-[#999] text-sm">{formatRelativeTime(i.lastCheckAt)}</span>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        render: (i: InstanceUpdateInfo) => (
          <button
            onClick={() => handleTriggerUpdate(i.instanceId)}
            disabled={
              triggeringId === i.instanceId ||
              i.updateStatus === "downloading" ||
              i.updateStatus === "updating"
            }
            className="px-2.5 py-1 bg-blue-500/10 text-blue-400 rounded text-xs font-medium hover:bg-blue-500/20 disabled:opacity-40 transition-colors"
          >
            {triggeringId === i.instanceId ? "Triggering..." : "Trigger Update"}
          </button>
        ),
      },
    ],
    [triggeringId]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Update Management</h1>
        <p className="text-[#999] text-sm mt-1">Publish versions and monitor fleet update status</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
          <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Target Version</p>
          <p className="text-lg font-mono text-[#f5f5f5]">{targetVersion ?? "None"}</p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
          <p className="text-xs text-[#999] uppercase tracking-wider mb-1">On Latest</p>
          <p className="text-lg font-mono text-green-400">
            {stats.onLatest} <span className="text-[#666] text-sm">/ {stats.total}</span>
          </p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
          <p className="text-xs text-[#999] uppercase tracking-wider mb-1">In Progress</p>
          <p className="text-lg font-mono text-blue-400">{stats.inProgress}</p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
          <p className="text-xs text-[#999] uppercase tracking-wider mb-1">Errors</p>
          <p className="text-lg font-mono text-red-400">{stats.withErrors}</p>
        </div>
      </div>

      {/* Publish + Quick Actions Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Publish Form */}
        <div className="col-span-2 bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
          <h2 className="text-sm font-semibold text-[#f5f5f5] mb-4">Publish New Version</h2>
          <form onSubmit={handlePublish} className="space-y-3">
            <div>
              <label className="block text-[#999] text-xs mb-1">Version *</label>
              <input
                type="text"
                value={publishFormVersion}
                onChange={(e) => {
                  setPublishFormVersion(e.target.value);
                  setPublishSuccess(false);
                  setPublishError(null);
                }}
                placeholder="e.g. 1.2.3"
                required
                className="w-full px-3 py-2 border border-[#333] rounded text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500/50
                  bg-[#111] text-[#f5f5f5]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[#999] text-xs mb-1">Git Ref</label>
                <input
                  type="text"
                  value={publishGitRef}
                  onChange={(e) => setPublishGitRef(e.target.value)}
                  placeholder="e.g. main, v1.2.3, abc1234"
                  className="w-full px-3 py-2 border border-[#333] rounded text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50
                    bg-[#111] text-[#f5f5f5]"
                />
              </div>
              <div>
                <label className="block text-[#999] text-xs mb-1">Description</label>
                <input
                  type="text"
                  value={publishDescription}
                  onChange={(e) => setPublishDescription(e.target.value)}
                  placeholder="e.g. Bug fixes and improvements"
                  className="w-full px-3 py-2 border border-[#333] rounded text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500/50
                    bg-[#111] text-[#f5f5f5]"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                {publishSuccess && (
                  <span className="text-green-400 text-sm">Version published successfully</span>
                )}
                {publishError && <span className="text-red-400 text-sm">{publishError}</span>}
              </div>
              <button
                type="submit"
                disabled={publishing || !publishFormVersion.trim()}
                className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-40 transition-colors"
              >
                {publishing ? "Publishing..." : "Publish"}
              </button>
            </div>
          </form>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
          <h2 className="text-sm font-semibold text-[#f5f5f5] mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={fetchStatus}
              className="w-full px-4 py-2 bg-[#111] border border-[#333] text-[#f5f5f5] rounded-lg text-sm font-medium hover:bg-[#222] transition-colors"
            >
              Refresh Status
            </button>
            {targetVersion && (
              <p className="text-xs text-[#666] text-center">
                Current target: <span className="font-mono text-[#999]">{targetVersion}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Fleet Status Table */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg">
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-sm font-semibold text-[#f5f5f5]">Fleet Version Status</h2>
          <p className="text-xs text-[#666] mt-0.5">
            {stats.onLatest} of {stats.total} instances on latest version
          </p>
        </div>
        <DataTable
          columns={columns}
          data={instances}
          keyFn={(i) => i.instanceId}
          emptyMessage="No instances found"
        />
      </div>
    </div>
  );
}
